import { FETCH_OPTIONS, TIMESHEET_ENDPOINTS } from '../../config/constants';
import { TimesheetModel } from '../../models/models';

/**
 * Funzione per trasformare i dati dal backend al formato frontend
 */
export const trasformaRegistrazione = (reg: any): TimesheetModel => {
  // Parsing delle date in formato locale italiano
  const dataIngresso = new Date(reg.data_ingresso);
  const dataUscita = new Date(reg.data_uscita);
  
  // Estrai data e ora in formato locale italiano (CET/CEST)
  const formatLocaleItaliano = (date: Date) => {
    const options: Intl.DateTimeFormatOptions = {
      timeZone: 'Europe/Rome',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    };
    return new Intl.DateTimeFormat('it-IT', options).format(date);
  };
  
  const dataIngressoLocale = formatLocaleItaliano(dataIngresso);
  const dataUscitaLocale = formatLocaleItaliano(dataUscita);
  
  // Parse formato: "gg/mm/aaaa, hh:mm"
  const [dataStr, oraIngressoStr] = dataIngressoLocale.split(', ');
  const [, oraUscitaStr] = dataUscitaLocale.split(', ');
  const [giorno, mese, anno] = dataStr.split('/');
  const dataISO = `${anno}-${mese}-${giorno}`;
  
  return {
    id: reg.id,
    id_struttura: reg.id_struttura,
    id_operatore: reg.id_operatore,
    data: dataISO,
    oraInizio: oraIngressoStr,
    oraFine: oraUscitaStr,
    data_ingresso: reg.data_ingresso,
    data_uscita: reg.data_uscita,
    note: reg.note,
    external: reg.external ?? false,
    id_societa: reg.id_societa ?? 0
  };
};

/**
 * Carica le registrazioni dall'API per un operatore specifico in un intervallo di date
 */
export const fetchRegistrazioni = async (
  idOperatore: number,
  dataIngresso: string,
  dataUscita: string
): Promise<TimesheetModel[]> => {
  const res = await fetch(
    `${TIMESHEET_ENDPOINTS.GET_REGISTRAZIONI}?id_operatore=${idOperatore}&data_ingresso=${dataIngresso}&data_uscita=${dataUscita}`,
    FETCH_OPTIONS
  );
  if (!res.ok) {
    throw new Error('Errore nel caricamento delle registrazioni');
  }
  
  const data = await res.json();
  const registrazioniBackend = Array.isArray(data) ? data : data.registrazioni || [];
  
  // Trasforma ogni registrazione dal formato backend al formato frontend
  return registrazioniBackend.map(trasformaRegistrazione);
};

/**
 * Aggiunge una nuova registrazione tramite API
 */
export const addRegistrazione = async (registrazione: TimesheetModel): Promise<TimesheetModel> => {
  const res = await fetch(TIMESHEET_ENDPOINTS.ADD_REGISTRAZIONE, {
    method: 'POST',
    ...FETCH_OPTIONS,
    body: JSON.stringify(registrazione)
  });

  const response = await res.json().catch(() => null);

  if (!res.ok) {
    const msg = response?.detail || response?.message || `Errore ${res.status}`;
    throw new Error(msg);
  }
  
  return trasformaRegistrazione(response);
};

/**
 * Aggiorna una registrazione esistente tramite API
 */
export const updateRegistrazione = async (id: number, registrazione: TimesheetModel): Promise<TimesheetModel> => {
  const dataAggiornata = {
    id: registrazione.id,
    id_struttura: registrazione.id_struttura,
    id_operatore: registrazione.id_operatore,
    data_ingresso: registrazione.data_ingresso || `${registrazione.data}T${registrazione.oraInizio}:00`,
    data_uscita: registrazione.data_uscita || `${registrazione.data}T${registrazione.oraFine}:00`,
    note: registrazione.note,
    external: registrazione.external ?? false,
    id_societa: registrazione.id_societa
  };

  const endpoint = TIMESHEET_ENDPOINTS.UPDATE_REGISTRAZIONE.replace(':id', id.toString());
  const res = await fetch(endpoint, {
    method: 'PUT',
    ...FETCH_OPTIONS,
    body: JSON.stringify(dataAggiornata)
  });

  const response = await res.json().catch(() => null);

  if (!res.ok) {
    const msg = response?.detail || response?.message || `Errore ${res.status}`;
    throw new Error(msg);
  }

  return trasformaRegistrazione(response);
};

/**
 * Elimina una registrazione tramite API
 */
export const deleteRegistrazione = async (id: number): Promise<void> => {
  const endpoint = TIMESHEET_ENDPOINTS.DELETE_REGISTRAZIONE.replace(':id', id.toString());
  const res = await fetch(endpoint, {
    method: 'DELETE',
    ...FETCH_OPTIONS
  });

  if (!res.ok) {
    const response = await res.json().catch(() => null);
    const msg = response?.detail || response?.message || `Errore ${res.status}`;
    throw new Error(msg);
  }
};
